// src/utils/tabHeaderUtils.js
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import SelectedIcon from 'src/components/selected-icon'
import React from 'react'
import { Typography } from '@material-ui/core'
import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'

// Constants
export const DEFAULT_ICON_SIZE = 16

// Model Update Functions - Now follows the same pattern as button
export const createTabHeader = (
  writer,
  {
    tabTitle,
    textColor,
    backgroundColor,
    selectedIcon,
    selectedIconName,
    tabIndex,
    isActive,
  },
) => {
  const tabHeader = writer.createElement('tabHeader', {
    tabTitle,
    textColor,
    backgroundColor,
    icon: selectedIcon,
    tabIndex,
    isActive,
  })

  // You can add child elements here if needed, similar to how button has text and icon
  // For now, assuming tabHeader is self-contained

  return {
    tabHeader,
  }
}

export const handleTabHeaderUpdate = (editor, position, tabHeaderProps) => {
  // First remove the existing tab header (same as button approach)
  editor.model.change(writer => {
    const existingTabHeader = position.parent.getChild(position.offset)
    if (existingTabHeader && existingTabHeader.name === 'tabHeader') {
      writer.remove(existingTabHeader)
    }
  })

  editor.model.change(writer => {
    const tabHeader = writer.createElement('tabHeader', {
      tabTitle: tabHeaderProps.tabTitle,
      textColor: tabHeaderProps.textColor,
      backgroundColor: tabHeaderProps.backgroundColor,
      icon: tabHeaderProps.icon,
      tabIndex: tabHeaderProps.tabIndex || '1',
      isActive: tabHeaderProps.isActive || 'false',
    })
    const header = writer.createElement('heading3')

    const textNode = writer.createText(tabHeaderProps.tabTitle)

    writer.append(header, tabHeader)
    writer.append(textNode, header)
    editor.model.insertContent(tabHeader, position)
  })
}

// Alternative approach: Update via selection if position doesn't work
export const handleTabHeaderUpdateViaSelection = (
  editor,
  selection,
  tabHeaderProps,
) => {
  editor.model.change(writer => {
    if (selection && selection.name === 'tabHeader') {
      // Remove the existing element
      writer.remove(selection)
    }
  })

  // Insert new tab header at the selection position
  editor.model.change(writer => {
    const tabHeader = writer.createElement('tabHeader', {
      tabTitle: tabHeaderProps.tabTitle,
      textColor: tabHeaderProps.textColor,
      backgroundColor: tabHeaderProps.backgroundColor,
      icon: tabHeaderProps.icon,
      tabIndex: tabHeaderProps.tabIndex || '1',
      isActive: tabHeaderProps.isActive || 'false',
    })

    editor.model.insertContent(tabHeader, editor.model.document.selection)
  })
}

// Title Change Handler
export const handleTabTitleChange = (
  event,
  {
    editor,
    position,
    textColor,
    backgroundColor,
    selectedIcon,
    selectedIconName,
    setTitle,
    tabIndex,
    isActive,
  },
) => {
  const newTitle = event.target.value
  setTitle(newTitle)

  handleTabHeaderUpdate(editor, position, {
    tabTitle: newTitle,
    textColor,
    backgroundColor,
    icon: selectedIcon,
    tabIndex,
    isActive,
  })
}

// Text Color Change Handler
export const handleTextColorChange = (
  event,
  {
    editor,
    position,
    title,
    backgroundColor,
    selectedIcon,
    selectedIconName,
    setTextColor,
    tabIndex,
    isActive,
  },
) => {
  const newColor = event.hex
  setTextColor(newColor)

  handleTabHeaderUpdate(editor, position, {
    tabTitle: title,
    textColor: newColor,
    backgroundColor,
    icon: selectedIcon,
    tabIndex,
    isActive,
  })
}

// Background Color Change Handler
export const handleBackgroundColorChange = (
  event,
  {
    editor,
    position,
    title,
    textColor,
    selectedIcon,
    selectedIconName,
    setBackgroundColor,
    tabIndex,
    isActive,
  },
) => {
  const newColor = event.hex
  setBackgroundColor(newColor)

  handleTabHeaderUpdate(editor, position, {
    tabTitle: title,
    textColor,
    backgroundColor: newColor,
    icon: selectedIcon,
    tabIndex,
    isActive,
  })
}

// Icon Handlers
export const handleIconSelect = (
  icon,
  {
    editor,
    position,
    title,
    textColor,
    backgroundColor,
    setSelectedIcon,
    setSelectedIconName,
    tabIndex,
    isActive,
  },
) => {
  setSelectedIcon(icon)
  setSelectedIconName(icon)

  handleTabHeaderUpdate(editor, position, {
    tabTitle: title,
    textColor,
    backgroundColor,
    icon: icon,
    tabIndex,
    isActive,
  })
}

export const handleIconDelete = ({
  editor,
  position,
  title,
  textColor,
  backgroundColor,
  setSelectedIcon,
  setSelectedIconName,
  tabIndex,
  isActive,
}) => {
  setSelectedIcon('')
  setSelectedIconName('lets-icons:add-round')

  handleTabHeaderUpdate(editor, position, {
    tabTitle: title,
    textColor,
    backgroundColor,
    icon: '',
    tabIndex,
    isActive,
  })
}

export const showIconSelector = ({
  editor,
  target,
  handleIconSelect,
  dispatch,
}) => {
  const handleClickOutside = event => {
    const messageBox = document.getElementById('message-box-1')
    if (messageBox && !messageBox.contains(event.target)) {
      document.removeEventListener('mousedown', handleClickOutside)
      dispatch(messageBoxHideAction('message-box-1'))
    }
  }

  document.addEventListener('mousedown', handleClickOutside)

  dispatch(
    messageBoxShowAction(
      MessageBoxContent({
        type: MessageBoxType.WARNING,
        title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
            <Typography>Select Tab Icon</Typography>
          </div>`,
        buttons: [],
        component: (
          <SelectedIcon
            editor={editor}
            target={target}
            onIconSelect={handleIconSelect}
          />
        ),
        variant: MessageBoxVariant.DRAGGABLE,
        callback: () => {},
      }),
    ),
  )
}

// Delete Tab Header
export const deleteTabHeader = (editor, position) => {
  editor.model.change(writer => {
    const existingTabHeader = position.parent.getChild(position.offset)
    if (existingTabHeader && existingTabHeader.name === 'tabHeader') {
      // Get the tab index of the header being deleted
      const deletedTabIndex = existingTabHeader.getAttribute('tabIndex') || '1'
      const wasActive = existingTabHeader.getAttribute('isActive') === 'true'

      // Find the tab wrapper (parent of tab headers container)
      const tabHeadersContainer = existingTabHeader.parent
      const tabWrapper = tabHeadersContainer.parent

      // Remove the tab header
      writer.remove(existingTabHeader)

      // Find and remove the corresponding tab content
      if (tabWrapper) {
        // Look for tab content container in the tab wrapper
        const tabContentContainer = Array.from(tabWrapper.getChildren()).find(
          child => child.name === 'tabContents',
        )

        if (tabContentContainer) {
          // Find the tab content with matching tabIndex
          const tabContentToDelete = Array.from(
            tabContentContainer.getChildren(),
          ).find(
            content => content.getAttribute('tabIndex') === deletedTabIndex,
          )

          if (tabContentToDelete) {
            writer.remove(tabContentToDelete)
          }
        }

        // Get remaining tab headers
        const remainingTabHeaders = Array.from(
          tabHeadersContainer.getChildren(),
        ).filter(child => child.name === 'tabHeader')

        // If the deleted tab was active, make the first remaining tab active
        if (wasActive && remainingTabHeaders.length > 0) {
          const firstTabHeader = remainingTabHeaders[0]
          const firstTabIndex = firstTabHeader.getAttribute('tabIndex')

          writer.setAttribute('isActive', 'true', firstTabHeader)

          // Make all other headers inactive
          remainingTabHeaders.slice(1).forEach(header => {
            writer.setAttribute('isActive', 'false', header)
          })

          // Get remaining tab contents
          const remainingTabContents = tabContentContainer
            ? Array.from(tabContentContainer.getChildren()).filter(
                child => child.name === 'tabContent',
              )
            : []

          // Update tab contents - make matching content active, others inactive
          remainingTabContents.forEach(content => {
            const contentTabIndex = content.getAttribute('tabIndex')
            writer.setAttribute(
              'isActive',
              contentTabIndex === firstTabIndex ? 'true' : 'false',
              content,
            )
          })
        }

        // Move selection to a safe position to prevent mapper null parent error
        if (remainingTabHeaders.length > 0) {
          writer.setSelection(remainingTabHeaders[0], 'on')
        } else if (tabWrapper) {
          writer.setSelection(tabWrapper, 'on')
        }
      }
    }
  })
}

// Get Initial State
export const getInitialTabHeaderState = selection => {
  if (selection && selection.name === 'tabHeader') {
    return {
      selectedIcon: selection.getAttribute('icon') || '',
      selectedIconName:
        selection.getAttribute('icon') || 'lets-icons:add-round',
      title: selection.getAttribute('tabTitle') || '',
      textColor: selection.getAttribute('textColor') || '',
      backgroundColor: selection.getAttribute('backgroundColor') || '',
      tabIndex: selection.getAttribute('tabIndex') || '1',
      isActive: selection.getAttribute('isActive') || 'false',
    }
  }

  return {
    selectedIcon: '',
    selectedIconName: 'lets-icons:add-round',
    title: '',
    textColor: '',
    backgroundColor: '',
    tabIndex: '1',
    isActive: 'false',
  }
}

// Helper function to get tab data from model element
export const getTabHeaderData = modelElement => {
  if (modelElement && modelElement.name === 'tabHeader') {
    return {
      tabIndex: modelElement.getAttribute('tabIndex'),
      isActive: modelElement.getAttribute('isActive'),
      icon: modelElement.getAttribute('icon'),
      backgroundColor: modelElement.getAttribute('backgroundColor'),
      textColor: modelElement.getAttribute('textColor'),
      tabTitle: modelElement.getAttribute('tabTitle'),
    }
  }
  return null
}
